import React from "react";
import { useLocation, Link } from "react-router-dom";
import { ChevronRight, BookOpen } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { useAuth } from "../lib/auth";
import { AlertBell } from "./AlertBell";
import { downloadAuthenticated } from "../lib/download";

const ROUTE_LABELS = {
    "": "Dashboard",
    pec: "PEC",
    inbox: "In arrivo",
    analyses: "Analisi",
    upload: "Nuova analisi",
    reviews: "Coda revisioni",
    senders: "Mittenti",
    suppliers: "Fornitori",
    ibans: "IBAN autorizzati",
    rules: "Regole e alert",
    reports: "Report",
    audit: "Audit log",
    integrations: "Integrazioni",
    settings: "Impostazioni",
    admin: "Amministrazione",
    companies: "Aziende",
    users: "Utenti",
};

function buildBreadcrumbs(pathname) {
    const parts = pathname.split("/").filter(Boolean);
    const crumbs = [{ label: "Dashboard", href: "/" }];
    let acc = "";
    for (const p of parts) {
        acc += `/${p}`;
        const label = ROUTE_LABELS[p] || decodeURIComponent(p);
        crumbs.push({ label, href: acc });
    }
    // last one no href
    if (crumbs.length > 0) crumbs[crumbs.length - 1] = { ...crumbs[crumbs.length - 1], href: null };
    return crumbs;
}

export function Topbar() {
    const location = useLocation();
    const { user, logout } = useAuth();
    const crumbs = buildBreadcrumbs(location.pathname);

    const initials = (user?.full_name || user?.email || "?")
        .split(/\s+/)
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <header
            data-testid="topbar"
            className="h-16 bg-white border-b border-[#DCE3EE] sticky top-0 z-30 flex items-center justify-between px-6"
        >
            <nav className="flex items-center gap-1.5 text-sm">
                {crumbs.map((c, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-[#94A3B8]" />}
                        {c.href ? (
                            <Link
                                to={c.href}
                                className="text-[#64748B] hover:text-[#2563EB] transition-colors font-medium"
                            >
                                {c.label}
                            </Link>
                        ) : (
                            <span className="text-[#172033] font-semibold">{c.label}</span>
                        )}
                    </React.Fragment>
                ))}
            </nav>

            <div className="flex items-center gap-2">
                <AlertBell />

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            data-testid="user-menu-trigger"
                            className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-[#F5F7FB] transition-colors"
                        >
                            <div className="w-8 h-8 rounded-full bg-[#2563EB] text-white text-xs font-bold flex items-center justify-center">
                                {initials}
                            </div>
                            <div className="text-left hidden sm:block">
                                <div className="text-sm font-medium text-[#172033] leading-tight">
                                    {user?.full_name || user?.email}
                                </div>
                                <div className="text-[11px] text-[#64748B] leading-tight">
                                    {user?.role_global === "super_admin" ? "Super Admin" : "Utente"}
                                </div>
                            </div>
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-white border border-[#DCE3EE] shadow-lg" align="end">
                        <DropdownMenuLabel>Account</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                            <Link to="/settings" data-testid="user-menu-settings">Impostazioni</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={() => downloadAuthenticated("/api/user-manual.pdf", "Manuale-IH-PEC-Guard.pdf")}
                            data-testid="user-menu-manual"
                            className="cursor-pointer"
                        >
                            <BookOpen className="w-4 h-4 mr-2 text-[#2563EB]" />
                            Scarica manuale operativo
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="text-[#DC2626]">
                            Esci
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </header>
    );
}

export default Topbar;
